import { LinearProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { shallow } from 'zustand/shallow';
import CaptionedLinearLoader from 'pages/calculator/MediaPlanning/components/Touchpoints/CaptionedLinearLoader/CaptionedLinearLoader';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Buttons,
  CalculateButton,
} from 'shared/components/ParametersForm/styles';
import { Countries } from 'shared/enums';
import useCalculateBudget from 'pages/calculator/MediaPlanning/Plan/Countries/hooks/useCalculateBudget';
import usePlanStore from 'store/planStore';
import { useCountriesDataStore } from 'store/countriesDataStore';
import { Touchpoints } from 'pages/calculator/MediaPlanning/Plan/enums';
import addIsIncludedFlagToCalculatedBudget from 'pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/Digital/utils/addIsIncludedFlagToCalculatedBudget';
import { useParams } from 'react-router-dom';
import YouTubeAccordion from './YouTubeAccordion/YouTubeAccordion';
import MetaAccordion from './MetaAccordion/MetaAccordion';
import TiktokAccordion from './TiktokAccordion/TiktokAccordion';
import EstimatedBudgetsTable from '../EstimatedBudgetsTable/EstimatedBudgetsTable';
import useEstimatedBudgetColumns from '../EstimatedBudgetsTable/hooks/useEstimatedBudgetColumns';
import ResetToDefaultsDialog from '../../CountryPanel/ResetToDefaultsDialog/ResetToDefaultsDialog';
import AddedToPlanSnackbar from './AddedToPlanSnackbar/AddedToPlanSnackbar';
import useCalculateOneReach from '../../hooks/useCalculateOneReach';
import useMetaPredictionsValidity from '../../../PlanSummary/OverridingBookingFlow/hooks/useMetaPredictionsValidity';
import MetaBookingModal from '../../../../MetaBooking/modals/MetaBookingModal';
import MetaAlertsInfoBox from './MetaAlertsInfoBox/MetaAlertsInfoBox';
import useMetaPredictionStatuses from '../../../PlanSummary/OverridingBookingFlow/hooks/useMetaPredictionStatuses';
import { Touchpoint } from '../../../../../../../api/services/mediaPlanning/touchpoints';
import { isRecommendedTargetGroupMissing } from '../../../../../../../shared/utils';
import RecommendedTargetGroupMissingModal from '../../../../common/RecommendedTargetGroupMissingModal/RecommendedTargetGroupMissingModal';

interface DigitalProps {
  availableTouchpoints: Touchpoint[];
  isFetchingAvailableTouchpoints: boolean;
}

export default function Digital({
  availableTouchpoints,
  isFetchingAvailableTouchpoints,
}: DigitalProps) {
  const [showResetToDefaultsDialog, setShowResetToDefaultsDialog] =
    useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [metaValidityDialogOpen, setMetaValidityDialogOpen] = useState(false);
  const [
    showRecommendedGroupMissingModal,
    setShowRecommendedGroupMissingModal,
  ] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();

  const touchpointNames = availableTouchpoints.map(
    (touchpoint) => touchpoint.TouchpointName
  );

  const {
    selectedCountry,
    selectedCountryISOCode,
    generalInfoState,
    countryForms,
    alreadySavedPlanId,
    resetCalculatedBudget,
    resetSavedCalculatedBudget,
    resetCountryFormConfig,
    saveDigitalCountryFormState,
    storeDigitalCountryFormState,
  } = usePlanStore();

  const { asyncCalculateOneReach } = useCalculateOneReach();
  const savedCountryForms = usePlanStore(
    (state) => state.savedCountryForms,
    shallow
  );

  const optionalIds = useCountriesDataStore(
    (state) => state[selectedCountryISOCode as Countries]?.optionalIds,
    shallow
  );

  const updateOptionalIds = useCountriesDataStore(
    (state) => state[selectedCountryISOCode as Countries]?.updateOptionalIds,
    shallow
  );

  const countryForm = countryForms[selectedCountry!];

  const queryClient = useQueryClient();

  const {
    data: pricingData,
    isLoading: isMetaPredictionsLoading,
    getMetaPredictions,
  } = useMetaPredictionsValidity(setMetaValidityDialogOpen);

  const { getMetaPredictionStatuses } = useMetaPredictionStatuses();

  const { calculateBudget, isLoading: isCalculatingBudget } =
    useCalculateBudget(getMetaPredictions, getMetaPredictionStatuses);

  const estimatedBudgetTableColumns = useEstimatedBudgetColumns(
    countryForms[selectedCountry!].calculatedBudget?.[0]?.budget.currency ??
      'PLN'
  );

  const isGoldenRuleViolated =
    (touchpointNames.includes(Touchpoints.youtube) &&
      countryForm.youTubeConfig?.isGoldenRuleMet === false) ||
    (touchpointNames.includes(Touchpoints.meta) &&
      countryForm.metaConfig?.isGoldenRuleMet === false) ||
    (touchpointNames.includes(Touchpoints.tiktok) &&
      countryForm.tiktokConfig?.isGoldenRuleMet === false);

  const isMetaTargetGroupResolved = !!(
    countryForm.metaConfig?.targetGroup &&
    countryForm.metaConfig?.targetGroups?.includes(
      countryForm.metaConfig.targetGroup
    )
  );

  const isMetaConsiderationMissingPartners =
    touchpointNames.includes(Touchpoints.meta) &&
    (countryForm.metaConfig?.partnerBudgetShareConsideration ?? 0) > 0 &&
    (countryForm.metaConfig?.partners?.length ?? 0) === 0;

  const isRecommendedGroupMissingForDigital =
    (touchpointNames.includes(Touchpoints.youtube) &&
      !countryForm.youTubeConfig?.targetGroup &&
      isRecommendedTargetGroupMissing(
        countryForm.youTubeConfig?.recommendedTargetGroup,
        countryForm.youTubeConfig?.targetGroups,
        (group) => group.targetGroup
      )) ||
    (touchpointNames.includes(Touchpoints.meta) &&
      !isMetaTargetGroupResolved &&
      isRecommendedTargetGroupMissing(
        countryForm.metaConfig?.recommendedTargetGroup,
        countryForm.metaConfig?.targetGroups
      )) ||
    (touchpointNames.includes(Touchpoints.tiktok) &&
      !countryForm.tiktokConfig?.targetGroup &&
      isRecommendedTargetGroupMissing(
        countryForm.tiktokConfig?.recommendedTargetGroup,
        countryForm.tiktokConfig?.targetGroups,
        (group) => group.targetGroup
      ));

  useEffect(() => {
    if (isRecommendedGroupMissingForDigital) {
      setShowRecommendedGroupMissingModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryForm.youTubeConfig?.targetGroups,
    countryForm.metaConfig?.targetGroups,
    countryForm.tiktokConfig?.targetGroups,
  ]);

  const handleCalculateBudget = async () => {
    calculateBudget({
      availableTouchpoints: touchpointNames,
    });
    updateOptionalIds({});
  };

  const handleResetToDefaults = () => {
    resetCalculatedBudget(selectedCountry!);
    resetSavedCalculatedBudget(selectedCountry!);
    resetCountryFormConfig(selectedCountry!);
    queryClient.invalidateQueries({
      queryKey: [
        'fanpages',
        selectedCountry,
        selectedCountryISOCode,
        generalInfoState.division,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'fieldConfig-youtube',
        selectedCountry,
        savedCountryForms[selectedCountry!],
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'fieldConfig-meta',
        selectedCountry,
        savedCountryForms[selectedCountry!],
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'fieldConfig-tiktok',
        selectedCountry,
        savedCountryForms[selectedCountry!],
      ],
    });
    setShowResetToDefaultsDialog(false);
  };

  const handleAddToPlan = async () => {
    if (!selectedCountry) {
      return;
    }

    const budgetWithIsIncludedInCalculationsFlag =
      addIsIncludedFlagToCalculatedBudget(
        optionalIds,
        countryForms[selectedCountry].calculatedBudget
      );

    const filteredBudget = budgetWithIsIncludedInCalculationsFlag?.filter(
      (row) => row.isIncludedInCalculations
    );
    usePlanStore.setState(
      (state) => ({
        countryForms: {
          ...state.countryForms,
          [selectedCountry]: {
            ...state.countryForms[selectedCountry],
            savedCalculatedBudget: filteredBudget ?? [],
            calculatedBudget: budgetWithIsIncludedInCalculationsFlag ?? [],
          },
        },
      }),
      false
    );
    saveDigitalCountryFormState(selectedCountry!);
    storeDigitalCountryFormState(selectedCountry);
    const hasMetaAwareness = availableTouchpoints.some(
      (touchpoint) =>
        touchpoint.TouchpointName === 'META' &&
        touchpoint.touchpointFunnel.toLowerCase().includes('awareness')
    );

    if (selectedCountryISOCode === Countries.POLAND && hasMetaAwareness) {
      await asyncCalculateOneReach();
    }

    saveDigitalCountryFormState(selectedCountry!);
    storeDigitalCountryFormState(selectedCountry);
    setShowSnackbar(true);
  };

  if (isFetchingAvailableTouchpoints) {
    return (
      <Box
        sx={{
          width: '100%',
          mt: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <LinearProgress sx={{ width: '300px' }} />
      </Box>
    );
  }

  if (!isFetchingAvailableTouchpoints && availableTouchpoints.length === 0) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">Missing touchpoints for this setup</Typography>
      </Box>
    );
  }

  return (
    <>
      {touchpointNames.includes(Touchpoints.youtube) && <YouTubeAccordion />}
      {touchpointNames.includes(Touchpoints.meta) && <MetaAccordion />}
      {touchpointNames.includes(Touchpoints.tiktok) && <TiktokAccordion />}
      {!isCalculatingBudget && (
        <MetaAlertsInfoBox
          alerts={{
            main: countryForms[selectedCountry!].metaConfig
              ?.metaPredictionStatuses?.mainPredictionStatus,
            partial:
              countryForms[selectedCountry!].metaConfig?.metaPredictionStatuses
                ?.partialPredictionStatus,
          }}
        />
      )}
      {availableTouchpoints.length > 0 && (
        <Buttons item xs sx={{ fontWeight: 700, mt: '40px', pb: '40px' }}>
          <CalculateButton
            sx={{
              outline: 1,
              minWidth: '9rem',
              borderRadius: '30px',
              marginRight: '33px',
            }}
            variant="outlined"
            onClick={() => {
              setShowResetToDefaultsDialog(true);
            }}
            disabled={
              countryForm.metaConfig?.reservationStatus === 'reserved' ||
              countryForm.metaConfig?.reservationStatus === 'partially-reserved'
            }
          >
            Reset to defaults
          </CalculateButton>
          <CalculateButton
            sx={{
              borderRadius: '30px',
              marginRight: '24px',
            }}
            onClick={handleCalculateBudget}
            variant="contained"
            disabled={
              isGoldenRuleViolated ||
              isRecommendedGroupMissingForDigital ||
              isMetaConsiderationMissingPartners
            }
          >
            {(countryForms[selectedCountry!].calculatedBudget?.length ?? 0) > 0
              ? 'Recalculate'
              : 'Calculate'}
          </CalculateButton>
        </Buttons>
      )}
      {isCalculatingBudget && (
        <CaptionedLinearLoader
          isScrollable
          caption={
            <Typography textAlign="center">
              <Trans i18nKey="loaders.gettingBudgetData" />
            </Typography>
          }
          sx={{ height: '340px' }}
        />
      )}
      {(countryForms[selectedCountry!].calculatedBudget?.length ?? 0) > 0 &&
        !isCalculatingBudget && (
          <EstimatedBudgetsTable
            columns={estimatedBudgetTableColumns}
            rows={countryForms[selectedCountry!].calculatedBudget!}
          />
        )}

      <Buttons item xs sx={{ fontWeight: 700, mt: '40px', pb: '40px' }}>
        {(countryForms[selectedCountry!].calculatedBudget?.length ?? 0) > 0 &&
          !isCalculatingBudget && (
            <CalculateButton
              sx={{
                borderRadius: '30px',
                marginRight: '24px',
              }}
              onClick={handleAddToPlan}
              type="submit"
              variant="contained"
            >
              Add to plan
            </CalculateButton>
          )}
      </Buttons>
      <AddedToPlanSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />
      <ResetToDefaultsDialog
        onClose={() => setShowResetToDefaultsDialog(false)}
        onSuccess={handleResetToDefaults}
        open={showResetToDefaultsDialog}
        text="Are you sure you want to clear this form?"
      />
      <MetaBookingModal
        open={metaValidityDialogOpen}
        onClose={() => setMetaValidityDialogOpen(false)}
        onSuccess={() => setMetaValidityDialogOpen(false)}
        header={t('mediaPlanning.metaBooking.modals.validityCheck.header')}
        text={t(
          'mediaPlanning.metaBooking.modals.validityCheck.text.hasBeenUpdated'
        )}
        isValidityCheck
        pricingData={pricingData}
        isInformationalOnly
        isLoading={isMetaPredictionsLoading}
      />
      <RecommendedTargetGroupMissingModal
        open={showRecommendedGroupMissingModal}
        onClose={() => setShowRecommendedGroupMissingModal(false)}
      />
    </>
  );
}
